import { useMemo, useState } from 'react'
import { Input } from '@heroui/react'
import { assignableServiceIds, moveItem, putGroup, putQueue } from '../../lib/profilesDoc'
import { nameIn } from '../../lib/staffText'
import type { OperationDoc, ProfileProblem, ProfileQueue, Service, ServiceGroup } from '../../lib/types'
import { Icon } from '../../ui/icons'
import { useStaffText } from '../../ui/StaffText'
import { MoveButtons } from '../recordParts'
import { Confirm } from '../sectionKit'
import { Pick, Tick } from '../setupKit'
import { QUEUE_FIELDS, QueueFields } from './QueueSheet'
import { ProfileModal, cardProblems, queueOptions, useLiveProblems, useProblemSlots } from './sheetParts'

/** A service group: name, services, default queue and its rules' order. A new group edits its own queue here too. */
export function GroupSheet({
  doc,
  group,
  freshQueue,
  services,
  problems,
  readOnly,
  onApply,
  onRemove,
  onClose,
}: {
  doc: OperationDoc
  group: ServiceGroup
  /** The queue made with a new group, while it is still that group's alone. */
  freshQueue?: ProfileQueue
  services: Service[]
  problems: ProfileProblem[]
  readOnly: boolean
  onApply: (g: ServiceGroup, q?: ProfileQueue) => void
  onRemove: () => void
  onClose: () => void
}) {
  const { lang, say } = useStaffText()
  const [name, setName] = useState(group.name)
  const [serviceIds, setServiceIds] = useState(group.serviceIds)
  const [defaultQueueId, setDefaultQueueId] = useState(group.defaultQueueId)
  const [segments, setSegments] = useState(group.segments)
  const [queue, setQueue] = useState(freshQueue)
  // The new queue's fields show while it is still the default.
  const ownQueue = queue && defaultQueueId === queue.id ? queue : undefined

  const next: ServiceGroup = { ...group, name, serviceIds, defaultQueueId, segments }
  const trial = useMemo(() => {
    const d = putGroup(doc, next)
    return queue ? putQueue(d, queue) : d
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc, group, name, serviceIds, defaultQueueId, segments, queue])
  const live = useLiveProblems(trial)
  const slots = useProblemSlots(cardProblems(live, 'group', group.id, problems), ['name', 'serviceIds', 'defaultQueueId', 'segments'])
  const queueSlots = useProblemSlots(ownQueue ? cardProblems(live, 'queue', ownQueue.id, []) : [], QUEUE_FIELDS)
  const segmentFlagged = (id: string) => live.some((p) => p.card.kind === 'segment' && p.card.id === id && !p.warning)

  const assignable = new Set(assignableServiceIds(doc, group.id, services.map((s) => s.id)))
  const listed = services.filter((s) => assignable.has(s.id) && (s.active || serviceIds.includes(s.id)))
  const strays = serviceIds.filter((id) => !services.some((s) => s.id === id))
  const toggle = (id: string, on: boolean) =>
    setServiceIds((ids) => (on ? [...ids.filter((x) => x !== id), id] : ids.filter((x) => x !== id)))

  const queues = queueOptions(
    trial.queues.filter((q) => q.type === 'normal' || q.id === defaultQueueId),
    lang,
  )

  return (
    <ProfileModal
      title={name.trim() || say('profiles.group')}
      readOnly={readOnly}
      onClose={onClose}
      onDone={() => onApply({ ...next, name: name.trim() }, ownQueue)}
      extra={
        !readOnly && (
          <Confirm
            ask={say('profiles.gs.remove')}
            question={say('profiles.gs.removeAsk')}
            yes={say('profiles.sheet.remove')}
            keep={say('profiles.sheet.keep')}
            onYes={onRemove}
          />
        )
      }
    >
      {slots.head}
      <fieldset className="pf-sheet" disabled={readOnly}>
        <label className="field">
          <span>{say('profiles.sheet.name')}</span>
          <Input fullWidth autoFocus value={name} aria-invalid={slots.has('name') || undefined} onChange={(e) => setName(e.target.value)} />
        </label>
        {slots.at('name')}

        <fieldset className="field">
          <legend>{say('profiles.sheet.services')}</legend>
          {listed.length === 0 && strays.length === 0 ? (
            <small>{say('profiles.gs.noServices')}</small>
          ) : (
            <div className="setup-ticks pf-ticks">
              {listed.map((s) => (
                <Tick key={s.id} checked={serviceIds.includes(s.id)} disabled={readOnly} onChange={(on) => toggle(s.id, on)}>
                  {nameIn(s.name, lang, s.code)}
                </Tick>
              ))}
              {strays.map((id) => (
                <Tick key={id} checked disabled={readOnly} onChange={() => toggle(id, false)}>
                  <span className="pf-gone">{say('profiles.sheet.serviceGone')}</span>
                </Tick>
              ))}
            </div>
          )}
        </fieldset>
        {slots.at('serviceIds')}

        <label className="field">
          <span>{say('profiles.gs.defaultQueue')}</span>
          <Pick label={say('profiles.gs.defaultQueue')} value={defaultQueueId} onChange={setDefaultQueueId} options={queues} disabled={readOnly} />
        </label>
        {slots.at('defaultQueueId')}
      </fieldset>

      {ownQueue && (
        <section className="pf-modal__part" aria-label={say('profiles.queue')}>
          {queueSlots.head}
          <QueueFields draft={ownQueue} set={(patch) => setQueue((q) => (q ? { ...q, ...patch } : q))} slots={queueSlots} readOnly={readOnly} compact />
        </section>
      )}

      {segments.length > 1 && (
        <section className="field">
          <span className="pf-sheet__label">{say('profiles.gs.segments')}</span>
          <ol className="pf-seglist">
            {segments.map((s, i) => (
              <li key={s.id} className="pf-seglist__row">
                <span className="pf-seglist__n">{i + 1}</span>
                <span className="pf-seglist__name">
                  <span>{s.name.trim() || say('profiles.segment')}</span>
                  {segmentFlagged(s.id) && <Icon name="alert" size={16} />}
                </span>
                <MoveButtons
                  name={s.name}
                  first={i === 0}
                  last={i === segments.length - 1}
                  disabled={readOnly}
                  onMove={(by) => setSegments((list) => moveItem(list, i, i + by))}
                />
              </li>
            ))}
          </ol>
        </section>
      )}
      {slots.at('segments')}
    </ProfileModal>
  )
}
